import { useEffect, useRef, useState } from 'react';
import { Item } from '@baemingo/table-events/dist/types';
import { exactMatch } from '~Utils/combineArticles';
import { LocalVariation } from '~Types';
import useAddChequeItems from './useAddChequeItems';

/** A fresh object per added item so the same row can re-trigger its animation */
export interface AddedItemEvent {
  token: number;
  pulseId?: string;
  flashId?: string;
}

interface Args {
  chequeId: string | null | undefined;
  tableId: string;
  tableName: string;
  /** All items on the cheque */
  items: Item[];
  selectedItemIds: string[];
  setSelectedItemIds: React.Dispatch<React.SetStateAction<string[]>>;
  setAddedItemEvent: (event: AddedItemEvent | null) => void;
  compressedCart: boolean;
  /** False e.g. once the cheque has payments */
  canAdd: boolean;
  canRemove: boolean;
  /** The scene decides if its modification popup must be shown before copying */
  requiresModifications: (sourceItem: Item) => boolean;
  /** Called instead of duplicating when modifications are required */
  onRequestModifications: (sourceItem: Item, count: number) => void;
}

/**
 * Owns the +/- quantity stepper for the selected cheque line. Nothing is added
 * or removed until the pending count is confirmed.
 */
export default function useChequeItemQuantity({
  chequeId,
  tableId,
  tableName,
  items,
  selectedItemIds,
  setSelectedItemIds,
  setAddedItemEvent,
  compressedCart,
  canAdd,
  canRemove,
  requiresModifications,
  onRequestModifications,
}: Args) {
  const addChequeItems = useAddChequeItems();

  /** Positive = copies to add, negative = selected lines to remove */
  const [pending, setPending] = useState(0);
  /** Ids handed to the remove modal once a negative count is confirmed */
  const [pendingRemoveIds, setPendingRemoveIds] = useState<string[]>([]);

  // Guards against the underlying touch/click handlers both firing for a single tap
  const lastTapRef = useRef(0);
  const guardDoubleTap = () => {
    const now = Date.now();
    if (now - lastTapRef.current < 50) {
      return false;
    }
    lastTapRef.current = now;
    return true;
  };

  // Only offer the stepper when every selected item is the exact same product
  // (either a single selection, or several merged copies selected together)
  const selectedItemObjects = items.filter((item) => selectedItemIds.includes(item.id));
  const canAdjust =
    selectedItemObjects.length > 0 &&
    selectedItemObjects.every((item) => exactMatch(item, selectedItemObjects[0]));
  const sourceItem = canAdjust ? selectedItemObjects[0] : null;

  // A new selection starts over from zero
  useEffect(() => {
    setPending(0);
  }, [selectedItemIds]);

  const canIncrement = canAdd && canAdjust;
  // Can never remove more lines than are selected
  const canDecrement = canRemove && canAdjust && pending > -selectedItemObjects.length;

  const increment = () => {
    if (canIncrement && guardDoubleTap()) {
      setPending((prev) => prev + 1);
    }
  };

  const decrement = () => {
    if (canDecrement && guardDoubleTap()) {
      setPending((prev) => prev - 1);
    }
  };

  /** Adds `count` copies of `sourceItem` and animates the affected row(s) */
  const duplicate = (
    source: Item,
    count: number,
    variations: LocalVariation[],
    removedVariations: LocalVariation[],
  ) => {
    if (!chequeId || count < 1) {
      return;
    }

    const newIds = addChequeItems.duplicate({
      sourceItem: source,
      count,
      variations,
      removedVariations,
      chequeId,
      tableId,
      tableName,
    });

    if (compressedCart) {
      // The copies may have different variations than `source`, in which case
      // they won't merge into that same compressed row. Figure out which row
      // they will actually land on so we animate the right one.
      const variationNames = variations.filter((v) => v.price > 0).map((v) => v.name);
      const candidate = { ...source, variations: variationNames, removedVariations };
      const mergedRowId = items.find((existing) => exactMatch(existing, candidate))?.id;
      const animationTargetId = mergedRowId ?? newIds[0];

      // The new ids must also be marked selected, otherwise they're treated as
      // "non-selected" which can wrongly let removal/payment be allowed
      setSelectedItemIds((prev) => [...prev, ...newIds]);
      setAddedItemEvent({
        token: Date.now(),
        pulseId: animationTargetId,
        flashId: animationTargetId,
      });
    } else {
      setSelectedItemIds([]);
      setAddedItemEvent({ token: Date.now(), flashId: newIds[0] });
    }
  };

  /** Commits the pending count: adds copies, or hands lines to the remove modal */
  const confirm = () => {
    if (!sourceItem || pending === 0 || !guardDoubleTap()) {
      return;
    }

    if (pending < 0) {
      setPendingRemoveIds(selectedItemObjects.slice(0, -pending).map((item) => item.id));
      return;
    }

    if (requiresModifications(sourceItem)) {
      onRequestModifications(sourceItem, pending);
      return;
    }

    duplicate(sourceItem, pending, sourceItem.richVariations, sourceItem.removedVariations);
  };

  return {
    canAdjust,
    sourceItem,
    pending,
    canIncrement,
    canDecrement,
    increment,
    decrement,
    confirm,
    duplicate,
    pendingRemoveIds,
    clearPendingRemove: () => setPendingRemoveIds([]),
  };
}
